import React from "react";
import logger from "@/lib/logger";
import { isInterruptedRequestError } from "@/main/common/ApiUtils";
import { notifyRestoredContentReady } from "@/main/navigation/NavigationHistoryState";
import {
    getCurrentNavigationCacheId,
    readNavigationListState,
    saveNavigationListState,
} from "@/main/navigation/NavigationListStateCache";
import { NativeResumeNewItemsPill, useNativeResumeHeadCheck } from "@/main/react/list/nativeResumeHeadCheck";

export type PaginatedListPhase =
    | "initial-loading"
    | "empty"
    | "ready"
    | "loading-more"
    | "end"
    | "initial-error"
    | "load-more-error";

export type PaginatedRenderPropsV2<T> = {
    items: T[];
    loading: boolean;
    hasMore: boolean;
    error: unknown | null;
    phase: PaginatedListPhase;
    visibleItemsCount: number;
    onLoadMore: () => Promise<void>;
    onRetry: () => Promise<void>;
    setItems: React.Dispatch<React.SetStateAction<T[]>>;
};

export type PaginatedStatusSlot<T> =
    | React.ReactNode
    | ((props: PaginatedRenderPropsV2<T>) => React.ReactNode);

export type PaginatedStatusSlots<T> = {
    initialLoading?: PaginatedStatusSlot<T>;
    empty?: PaginatedStatusSlot<T>;
    loadingMore?: PaginatedStatusSlot<T>;
    end?: PaginatedStatusSlot<T>;
    initialError?: PaginatedStatusSlot<T>;
    loadMoreError?: PaginatedStatusSlot<T>;
};

export type PaginatedListWrapperV2Props<
    T extends { paginationParameter?: P | null },
    P = string,
> = {
    fetchPage: (paginationParameter?: P) => Promise<T[]>;
    render: (props: PaginatedRenderPropsV2<T>) => React.ReactElement;
    getId: (item: T) => string | undefined;
    initialItems?: T[];
    statusSlots?: PaginatedStatusSlots<T>;
    getVisibleItemsCount?: (items: T[]) => number;
    paginationMode?: "scroll" | "button";
    /**
     * The page size the backend behind `fetchPage` actually uses, when the caller knows it.
     *
     * <p>Supplying it lets a FIRST page shorter than the page size settle exhaustion locally: a page
     * that came back short returned everything there was, whatever cursor its last row carries. Without
     * it the only signal available is the cursor, and a cursor is emitted for every row by construction
     * rather than only for rows that have a successor — so a complete short list advertises a next page
     * it does not have and offers a "Load more" that fetches nothing.
     *
     * <p>The bound is deliberately one-sided, matching the server twin
     * (`HeaderInboxService.hasMoreNotifications`): a page that ends exactly on the boundary still costs
     * one empty fetch, which is the cheaper error than hiding real rows.
     *
     * <p>**Optional on purpose.** A list that omits it is bit-for-bit unchanged, which is what lets
     * consumers adopt this one mount at a time instead of requiring every backend page size to be
     * established at once.
     */
    pageSize?: number;
    /**
     * How far ahead of the end of the list a load-more PREFETCH fires. Defaults to `"150%"`.
     *
     * <p>With `root: null` a percentage root margin resolves against the ROOT's own dimensions, so
     * `"150%"` means "one and a half viewport heights before the end" with no measuring code and no
     * resize listener. The fetch therefore starts while the reader is still a screen and a half away
     * and completes under reading time they were going to spend anyway.
     *
     * <p>This replaced a 600ms dwell timer on 2026-08-23. A *time* gate only avoids a request if the
     * reader blows past the bottom inside the window, so it charged the engaged reader who stops to
     * read and spared the one who flings. Runaway chaining is bounded by consumption instead — see
     * `sentinelLeftSinceLastFetchRef` below. Design:
     * designs/01-now/2026-08-23-feed-pagination-consumption-gate-design.md
     */
    paginationRootMargin?: string;
    /**
     * Opt this list into back-navigation restoration. When set, the accumulated pages are remembered
     * per history entry and re-seeded on a remount of the same entry, so Back rebuilds the list without
     * a network round-trip instead of dropping every page past the first.
     *
     * **This prop IS the rollout control** — there is no feature flag (rule 35). A surface without a
     * `restorationKey` is bit-for-bit unchanged, and removing the prop is the whole rollback.
     *
     * The value must identify the list WITHIN its page (the history entry supplies the rest of the
     * key), and it must change whenever the list's contents would differ — e.g.
     * `home:${feedType}-${orderingStyle}-${range}`.
     */
    restorationKey?: string;
    /**
     * Fired exactly ONCE per resolved page, in resolution order, for lists that want an impression
     * denominator (`feed_page_shown`). Optional and inert when absent.
     *
     * <p>It lives here rather than in each list's `render` callback because `render` runs on every
     * commit: a page-shown derived from render fires per re-render and silently inflates the
     * denominator, which then reads as a CTR regression the day the ranking changes. This callback is
     * driven by page *resolution* — the server-inlined seed, the initial fetch, and each load-more —
     * and is ref-deduped, so a re-render, a StrictMode double-invoke, or a re-run of the seeding effect
     * cannot re-fire it.
     *
     * <p>A back-navigation restore re-seeds every page the user had loaded in ONE commit. That reports
     * as a single `origin: "restored"` page rather than as N fresh impressions, because the user is
     * looking at a list they already saw — counting it again would inflate the denominator with
     * impressions nobody newly received.
     */
    onPageShown?: (info: {
        /** 1-based; 1 is the initial page. */
        pageIndex: number;
        /** The items that arrived in THIS page, not the accumulated list. */
        pageItems: T[];
        origin: "seed" | "restored" | "initial-fetch" | "load-more";
    }) => void;
    /**
     * Opt this list into the native-resume head check (round 12 slice 1m, D11/D28/A19). When the app comes
     * back after a real absence (`onNativeResume`, the bridge's 60s policy), page 1 is fetched in the
     * background and compared by the FIRST item's id:
     * <ul>
     *   <li>same head: nothing is written, nothing re-renders;</li>
     *   <li>new head, list top in view: the fresh page replaces the list silently;</li>
     *   <li>new head, reader scrolled into the list: the fresh page is held and a sticky pill with
     *       `newItemsLabel` offers it; tapping swaps it in and scrolls the list's top into view. Content never
     *       moves under the reader.</li>
     * </ul>
     * `newItemsLabel` is already localized by the consumer (`feed.resume.new_posts` / `feed.resume.new_replies`).
     * Absent means the list is bit-for-bit unchanged, the same opt-in shape as `restorationKey`.
     */
    nativeResumeRefresh?: {
        newItemsLabel: string;
        /**
         * FI-589: also run the head check on the WEB when the tab comes back after at least 60 s hidden. A
         * separate opt-in so the home feed, whose web refresh CTA was removed on purpose, stays native-only.
         */
        onWebTabReturn?: boolean;
    };
    /**
     * Round 40 D10 (`DASHBOARD-86`, `DASHBOARD-130`, `EMPTY-STATES-122`): the list's answer to "is there
     * anything here?", reported only once it is KNOWN. `true` when the list settles in its `empty` phase
     * (a completed load with zero visible items), `false` when it settles with at least one visible item.
     * Never fired while the first page is loading or after it failed: "not yet known" and "could not be
     * read" are not "empty", and a host that hid its controls on either would take the way out of an
     * error away. Fired once per change, read through a ref, so an inline arrow costs nothing.
     *
     * <p>The wrapper does not know whether the viewer narrowed the list; the host does, and it is the
     * host that turns this into "the UNFILTERED total is 0" by ignoring an empty report while a filter
     * is on. Absent means the list is bit-for-bit unchanged.
     */
    onEmptinessSettled?: (empty: boolean) => void;
    className?: string;
};

/**
 * How far ahead of the end of the list a prefetch fires. Percentage root margins resolve against the
 * root (the viewport, since `root: null`), so this is 1.5 viewport heights, on every device, with no
 * arithmetic. Deliberately NOT a pixel value: 200px was a quarter-screen on a phone and a fifth of
 * one on a desktop, i.e. "the reader has already arrived" on both.
 */
const DEFAULT_PAGINATION_ROOT_MARGIN = "150%";

/**
 * The end-of-list trigger, which is NEVER subject to the consumption gate. Fixed rather than a prop:
 * "the reader has actually run out of content" is not a tuning question, and making it configurable
 * would invite someone to disable the one path that guarantees a short page cannot deadlock the list.
 */
const BOTTOM_TRIGGER_ROOT_MARGIN = "0px";

type FailedRequest<P> = {
    kind: "initial" | "load-more";
    paginationParameter?: P;
};

function resolveStatusSlot<T>(
    slot: PaginatedStatusSlot<T> | undefined,
    props: PaginatedRenderPropsV2<T>,
): React.ReactNode {
    if (slot == null) {
        return null;
    }

    return typeof slot === "function"
        ? (slot as (props: PaginatedRenderPropsV2<T>) => React.ReactNode)(props)
        : slot;
}

function inferHasMoreFromItems<T extends { paginationParameter?: P | null }, P = string>(items: T[]): boolean {
    if (items.length === 0) {
        return false;
    }

    const last = items[items.length - 1];
    const cursor = last?.paginationParameter;
    if (cursor == null) {
        return false;
    }
    // A BLANK cursor is not a cursor. Nine backend queries emit `''::text AS pagination_parameter`
    // rather than NULL, and `'' != null`, so those lists advertised a next page that does not exist.
    // The blank is then read as "first page" server-side, so it degraded to a duplicate fetch that the
    // repeat-key guard stopped after one round — harmless, but wrong, and it makes `hasMore` lie to
    // every consumer that reads it (empty states, "load more" affordances, scroll sentinels).
    // Fixed here rather than in the nine native queries: one consumer covers all of them with no SQL
    // risk, and the `'' → NULL` migration becomes optional hygiene instead of the fix.
    return typeof cursor !== "string" || cursor.trim() !== "";
}

/**
 * The FIRST page's answer to "is there another page?" — the fetched one at the initial load, or the
 * seeded/restored one the effect below installs.
 *
 * A cursor is emitted for every row by construction, not only for rows that have a successor, so the
 * cursor-only predicate above really answers "the list is non-empty". A complete short list therefore
 * advertised a next page it did not have, and the surface offered a "Load more" whose only visible
 * effect was the control removing itself after an empty round-trip. A page shorter than the size that
 * was requested returned everything there was, whatever its last row carries.
 *
 * Two properties are load-bearing:
 * - **`pageSize` absent means today's behavior, exactly.** Adoption is per mount, so a consumer whose
 *   backend page size is not established is bit-for-bit unchanged.
 * - **The bound is one-sided**, matching the server twin `HeaderInboxService.hasMoreNotifications`
 *   (`items.size() >= PAGE_SIZE`): an exactly-full page still costs one empty fetch, which is the
 *   cheaper error than hiding real rows.
 *
 * Deliberately NOT applied to the load-more site, which answers from the raw page's tail cursor and
 * whether that cursor was already requested (FI-655), and deliberately NOT extended to a `length % pageSize` inference for a back-restored list: a restore
 * seeds the ACCUMULATED pages rather than one page, and its tail cursor is what the list resumes from.
 */
function inferHasMoreFromFirstPage<T extends { paginationParameter?: P | null }, P = string>(
    items: T[],
    pageSize: number | undefined,
): boolean {
    if (pageSize != null && pageSize > 0 && items.length < pageSize) {
        return false;
    }

    return inferHasMoreFromItems<T, P>(items);
}

/**
 * The cursor a list resumes from: the last cursor of the RAW page the server returned (FI-655).
 *
 * <p>Never the last item that survived dedupe. When a page's cursor-bearing tail repeats an id already on
 * screen, the last KEPT item carries a mid-page cursor, so the next request re-fetched rows the reader had
 * already seen and, once a whole page came back as repeats, ended the list with thousands of rows left.
 */
function tailCursorOf<T extends { paginationParameter?: P | null }, P = string>(items: T[]): P | undefined {
    const last = items.length ? items[items.length - 1] : null;
    return last == null ? undefined : (last.paginationParameter ?? undefined) as P | undefined;
}

export function PaginatedListWrapperV2<
    T extends { paginationParameter?: P | null },
    P = string,
>(props: PaginatedListWrapperV2Props<T, P>) {
    const {
        fetchPage,
        render,
        getId,
        initialItems,
        statusSlots,
        getVisibleItemsCount,
        pageSize,
        paginationMode = "scroll",
        paginationRootMargin = DEFAULT_PAGINATION_ROOT_MARGIN,
        restorationKey,
        onPageShown,
        nativeResumeRefresh,
        onEmptinessSettled,
        className,
    } = props;

    // Read the cache exactly ONCE, at mount, before the first commit — the point of the restore is that
    // the list reaches full height in its FIRST commit, with no fetch beat in between. Deliberately
    // empty deps: a later re-render must never re-consult the cache, or a list that has since paginated
    // would be reset to what it looked like when the entry was written.
    const restoration = React.useMemo<{ items: T[]; navigationId: string } | undefined>(() => {
        if (!restorationKey) {
            return undefined;
        }
        // `undefined` here is an UNSTAMPED history entry, which is a real reachable state (an entry from
        // before enhanced navigation shipped, or one whose write was refused). Caching is simply off for
        // this mount; the list falls through to exactly its pre-restoration behavior.
        const navigationId = getCurrentNavigationCacheId();
        const items = readNavigationListState<T>(navigationId, restorationKey);
        return navigationId && items ? { items, navigationId } : undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const restoredItems = restoration?.items;

    // SV-24 slice 4 — the ordering primitive. This commit rendered every restored card, so the document
    // can hold the saved offset NOW, before the browser paints; the pop's restore (started before this
    // island mounted, keyed on the same entry id) re-applies the offset synchronously and finishes.
    // Mount-only by construction: a later re-render is not a second restore, and a StrictMode
    // double-invoke finds the restore already finished and is a no-op in the channel.
    React.useLayoutEffect(() => {
        if (restoration) {
            notifyRestoredContentReady(restoration.navigationId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A restored list WINS over a server-inlined first page: both describe the same feed, but the cache
    // holds every page the user had loaded and the inline seed holds only page 1. They never combine.
    const effectiveInitialItems = restoredItems ?? initialItems;

    const [items, setItems] = React.useState<T[]>(effectiveInitialItems ?? []);
    const [loading, setLoadingState] = React.useState(effectiveInitialItems == null);
    const [hasMore, setHasMoreState] = React.useState(true);
    const [error, setErrorState] = React.useState<unknown | null>(null);
    const [initialLoadCompleted, setInitialLoadCompleted] = React.useState(effectiveInitialItems != null);

    const itemsRef = React.useRef<T[]>(items);
    const loadingRef = React.useRef<boolean>(loading);
    const hasMoreRef = React.useRef<boolean>(hasMore);
    const errorRef = React.useRef<unknown | null>(error);
    const isMountedRef = React.useRef(true);
    const sentinelRef = React.useRef<HTMLDivElement | null>(null);
    // Two observers on the SAME sentinel node, because `rootMargin` is per-observer and one cannot
    // express both distances: the prefetch trigger fires 1.5 viewports early and is consumption-gated,
    // the bottom trigger fires at the real end of the list and is never gated.
    const prefetchObserverRef = React.useRef<IntersectionObserver | null>(null);
    const bottomObserverRef = React.useRef<IntersectionObserver | null>(null);
    const requestedPaginationKeysRef = React.useRef<Set<string>>(new Set());
    // FI-655: the raw page's tail cursor, which is what `loadMore` pages from. Seeded from the initial
    // items, whose last item IS the raw tail (a seed and a restore are never deduped).
    const nextCursorRef = React.useRef<P | undefined>(tailCursorOf<T, P>(effectiveInitialItems ?? []));
    // Bumped when a load-more resolves with nothing new to append but a fresh cursor to follow. The
    // observers are keyed on `items.length`, which did not change, so without this they would never
    // deliver another edge and the list would sit at a bottom that never advances.
    const [dedupeOnlyPageCount, setDedupeOnlyPageCount] = React.useState(0);
    const lastFailedRequestRef = React.useRef<FailedRequest<P> | null>(null);

    /**
     * The consumption gate. A PREFETCH is allowed only when the sentinel has left the prefetch
     * observation rect since the last page was appended — i.e. the reader actually moved past the
     * content we already handed them.
     *
     * <p>Initialised `true`: nothing has been fetched yet this mount, so the first prefetch is free.
     * Set `false` on every append, and `true` on the observer's leave edge.
     *
     * <p>This is what stops request chaining, and it is tighter than the 600ms timer it replaced. The
     * prefetch observer is torn down and recreated on every `items.length` change, and `observe()`
     * re-delivers the current intersection state, so:
     * <ul>
     *   <li>a page TALLER than the prefetch distance (the normal case — 10 feed cards is ~2600-6000px
     *       against ~1200-1300px of prefetch distance) pushes the sentinel out of the rect, the
     *       initial callback reports not-intersecting, the gate re-arms, and the next fetch waits for
     *       the reader to scroll down to it. Consumption-bounded straight out of the geometry;</li>
     *   <li>a page SHORTER than the prefetch distance leaves the sentinel in view, the gate stays
     *       shut, and the chain stops dead. That runaway is the only case the old dwell timer was
     *       ever really protecting against.</li>
     * </ul>
     *
     * <p>⚠️ This gate may only ever suppress a PREFETCH. The bottom trigger below is deliberately
     * exempt, because case two above would otherwise park the reader at a bottom that never advances.
     */
    const sentinelLeftSinceLastFetchRef = React.useRef<boolean>(true);

    // Impression accounting for `onPageShown`. Held in refs, never in state: this must not participate
    // in rendering, and the whole point is that a re-render cannot produce a second impression.
    const pagesShownRef = React.useRef(0);
    const seedPageShownRef = React.useRef(false);
    // The callback is read through a ref so a caller passing an inline arrow does not churn the
    // identity of `fetchPageInternal` — which every pagination effect below depends on.
    const onPageShownRef = React.useRef(onPageShown);
    React.useEffect(() => {
        onPageShownRef.current = onPageShown;
    }, [onPageShown]);

    const reportPageShown = React.useCallback((
        pageItems: T[],
        origin: "seed" | "restored" | "initial-fetch" | "load-more",
    ) => {
        const report = onPageShownRef.current;
        if (!report) {
            return;
        }

        // An empty FIRST page is a real impression — the reader was shown an empty feed. An empty
        // later page is a pagination no-op and would only add noise to the denominator.
        if (pageItems.length === 0 && pagesShownRef.current > 0) {
            return;
        }

        pagesShownRef.current += 1;
        try {
            report({ pageIndex: pagesShownRef.current, pageItems, origin });
        } catch {
            // Instrumentation is side-effect-only; it may never break pagination.
        }
    }, []);

    const disconnectPaginationObservers = React.useCallback(() => {
        for (const ref of [prefetchObserverRef, bottomObserverRef]) {
            try {
                ref.current?.disconnect();
            } catch {
            } finally {
                ref.current = null;
            }
        }
    }, []);

    React.useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    React.useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    const setLoading = React.useCallback((value: boolean) => {
        loadingRef.current = value;
        setLoadingState(value);
    }, []);

    const setHasMore = React.useCallback((value: boolean) => {
        hasMoreRef.current = value;
        setHasMoreState(value);

        if (!value) {
            disconnectPaginationObservers();
        }
    }, [disconnectPaginationObservers]);

    const setError = React.useCallback((value: unknown | null) => {
        errorRef.current = value;
        setErrorState(value);
    }, []);

    const toPaginationRequestKey = React.useCallback((paginationParameter?: P) => {
        if (paginationParameter == null) {
            return "__NULL_PAGINATION_PARAMETER__";
        }
        if (typeof paginationParameter === "string") {
            return paginationParameter;
        }
        try {
            return JSON.stringify(paginationParameter);
        } catch {
            return String(paginationParameter);
        }
    }, []);

    const fetchPageInternal = React.useCallback(async (
        paginationParameter: P | undefined,
        requestKind: FailedRequest<P>["kind"],
    ): Promise<number> => {
        try {
            const list = await fetchPage(paginationParameter);
            if (!isMountedRef.current) {
                return 0;
            }

            const normalizedList = list.filter(Boolean);
            const isInitialLoad = paginationParameter == null && requestKind === "initial" && itemsRef.current.length === 0;

            setError(null);
            lastFailedRequestRef.current = null;

            if (isInitialLoad) {
                itemsRef.current = normalizedList;
                nextCursorRef.current = tailCursorOf<T, P>(normalizedList);
                // Content was just handed to the reader: shut the consumption gate. It reopens when the
                // sentinel leaves the prefetch rect, which for a full page is the observer's very next
                // callback and for a short one is only once the reader scrolls.
                sentinelLeftSinceLastFetchRef.current = false;
                setItems(normalizedList);
                setHasMore(inferHasMoreFromFirstPage<T, P>(normalizedList, pageSize));
                setInitialLoadCompleted(true);
                reportPageShown(normalizedList, "initial-fetch");
                return normalizedList.length;
            }

            const previousItems = itemsRef.current;
            const existing = new Set(previousItems.map((item) => getId(item)));
            const toAppend = normalizedList.filter((item) => !existing.has(getId(item)));
            const nextItems = [...previousItems, ...toAppend];

            // FI-655: follow the RAW page's tail cursor, and keep paging while it is one we have not
            // requested yet. A page whose new rows were all repeats still pages on; a page that hands
            // back an already-requested cursor stops here (the repeat-key guard in `loadMore` would stop
            // it one round later anyway).
            const rawTailCursor = tailCursorOf<T, P>(normalizedList);
            const rawTailIsFresh = rawTailCursor != null
                && !requestedPaginationKeysRef.current.has(toPaginationRequestKey(rawTailCursor));
            const nextHasMore = inferHasMoreFromItems<T, P>(normalizedList) && rawTailIsFresh;
            if (rawTailCursor != null) {
                nextCursorRef.current = rawTailCursor;
            }

            itemsRef.current = nextItems;
            if (toAppend.length > 0) {
                sentinelLeftSinceLastFetchRef.current = false;
            }
            setItems(nextItems);
            setHasMore(nextHasMore);
            if (toAppend.length === 0 && nextHasMore) {
                setDedupeOnlyPageCount((count) => count + 1);
            }
            if (requestKind === "initial") {
                setInitialLoadCompleted(true);
            }
            reportPageShown(toAppend, requestKind === "initial" ? "initial-fetch" : "load-more");
            return toAppend.length;
        } catch (err) {
            if (!isMountedRef.current || isInterruptedRequestError(err)) {
                throw err;
            }

            lastFailedRequestRef.current = { kind: requestKind, paginationParameter };
            setError(err);
            if (requestKind === "initial") {
                setInitialLoadCompleted(true);
            }
            throw err;
        }
    }, [fetchPage, getId, pageSize, reportPageShown, setError, setHasMore, toPaginationRequestKey]);

    const loadMore = React.useCallback(async (options?: {
        ignoreExistingError?: boolean;
        overridePaginationParameter?: P;
    }) => {
        const { ignoreExistingError = false, overridePaginationParameter } = options ?? {};

        if (loadingRef.current) {
            return;
        }
        if (!hasMoreRef.current) {
            return;
        }
        if (errorRef.current && !ignoreExistingError) {
            return;
        }

        const currentItems = itemsRef.current;
        const lastParam = overridePaginationParameter ?? (currentItems.length > 0 ? nextCursorRef.current : undefined);

        if (currentItems.length > 0 && lastParam == null) {
            logger.warn("[PaginatedListWrapperV2] stopping pagination because the last page has no paginationParameter");
            setHasMore(false);
            return;
        }

        let requestKey: string | null = null;
        if (currentItems.length > 0 && lastParam != null) {
            requestKey = toPaginationRequestKey(lastParam);
            if (requestedPaginationKeysRef.current.has(requestKey)) {
                logger.warn("[PaginatedListWrapperV2] stopping pagination because the same paginationParameter was requested again", requestKey);
                setHasMore(false);
                return;
            }
            requestedPaginationKeysRef.current.add(requestKey);
        }

        const requestKind: FailedRequest<P>["kind"] = currentItems.length === 0 ? "initial" : "load-more";
        let keepLoadingAfterInterruptedInitialRequest = false;

        setLoading(true);
        try {
            await fetchPageInternal(lastParam, requestKind);
        } catch (err) {
            const interrupted = isInterruptedRequestError(err);
            if (requestKey != null) {
                requestedPaginationKeysRef.current.delete(requestKey);
            }

            if (interrupted && requestKind === "initial" && isMountedRef.current) {
                keepLoadingAfterInterruptedInitialRequest = true;
            }

            if (isMountedRef.current && !interrupted) {
                logger.error("[PaginatedListWrapperV2] loadMore error", err);
            }
        } finally {
            if (isMountedRef.current && !keepLoadingAfterInterruptedInitialRequest) {
                setLoading(false);
            }
        }
    }, [fetchPageInternal, setHasMore, setLoading, toPaginationRequestKey]);

    /**
     * The one place either trigger turns into a fetch.
     *
     * <p>`reason` is the ONLY difference between them: a `"prefetch"` is additionally gated on the
     * reader having consumed the last page we appended, a `"bottom"` never is. Keeping both callers on
     * one function is deliberate — the ordinary loading/hasMore/error guards then cannot drift apart,
     * and the exemption stays a single readable line rather than a duplicated guard stack.
     */
    const triggerLoadMore = React.useCallback((reason: "prefetch" | "bottom") => {
        if (loadingRef.current || !hasMoreRef.current || errorRef.current) {
            return;
        }
        if (reason === "prefetch" && !sentinelLeftSinceLastFetchRef.current) {
            return;
        }

        void loadMore();
    }, [loadMore]);

    const fetchInitialPage = React.useCallback(async () => {
        if (loadingRef.current) {
            return;
        }

        setLoading(true);
        setError(null);
        lastFailedRequestRef.current = null;
        let keepLoadingAfterInterruptedRequest = false;

        try {
            await fetchPageInternal(undefined, "initial");
        } catch (err) {
            const interrupted = isInterruptedRequestError(err);

            if (interrupted && isMountedRef.current) {
                keepLoadingAfterInterruptedRequest = true;
            }

            if (isMountedRef.current && !interrupted) {
                logger.error("[PaginatedListWrapperV2] initial fetch error", err);
            }
        } finally {
            if (isMountedRef.current && !keepLoadingAfterInterruptedRequest) {
                setLoading(false);
            }
        }
    }, [fetchPageInternal, setError, setLoading]);

    const retry = React.useCallback(async () => {
        const failedRequest = lastFailedRequestRef.current;
        if (!failedRequest) {
            if (itemsRef.current.length === 0) {
                await fetchInitialPage();
            } else {
                await loadMore({ ignoreExistingError: true });
            }
            return;
        }

        setError(null);
        if (failedRequest.kind === "initial") {
            await fetchInitialPage();
            return;
        }

        await loadMore({
            ignoreExistingError: true,
            overridePaginationParameter: failedRequest.paginationParameter,
        });
    }, [fetchInitialPage, loadMore, setError]);

    // Branches on the EFFECTIVE seed, not on `initialItems`. Branching on the raw prop would send a
    // restored mount down the fetch path and wipe the list it had just rebuilt — the restore would be
    // undone by the component's own effect, one commit after it worked.
    React.useEffect(() => {
        requestedPaginationKeysRef.current.clear();
        lastFailedRequestRef.current = null;
        setError(null);

        nextCursorRef.current = tailCursorOf<T, P>(effectiveInitialItems ?? []);
        if (effectiveInitialItems == null) {
            setItems([]);
            setHasMore(true);
            setInitialLoadCompleted(false);
            setLoading(false);
            void fetchInitialPage();
            return;
        }

        setItems(effectiveInitialItems);
        setHasMore(inferHasMoreFromFirstPage<T, P>(effectiveInitialItems, pageSize));
        setInitialLoadCompleted(true);
        setLoading(false);

        // Guarded by a ref rather than by the dependency array: this effect re-runs on a StrictMode
        // double-invoke with an unchanged seed, and a second impression for the same page is precisely
        // the denominator inflation `onPageShown` exists to prevent.
        if (!seedPageShownRef.current) {
            seedPageShownRef.current = true;
            reportPageShown(effectiveInitialItems, restoredItems ? "restored" : "seed");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveInitialItems]);

    // Bank the accumulated pages against the CURRENT history entry, re-reading the id on every save
    // rather than capturing it at mount: the entry can be rewritten underneath us, and the id that
    // matters is the one live at the moment the content exists. Plain imperative writes — this cache is
    // not a store, has no subscribers, and cannot re-render anything (rule 31).
    React.useEffect(() => {
        if (!restorationKey || items.length === 0) {
            return;
        }

        saveNavigationListState(getCurrentNavigationCacheId(), restorationKey, items);
    }, [items, restorationKey]);

    React.useEffect(() => {
        if (paginationMode !== "scroll") {
            return;
        }

        const node = sentinelRef.current;
        if (!node || !hasMoreRef.current || errorRef.current) {
            return;
        }

        // The PREFETCH trigger. Fires 1.5 viewports before the end of the list, so the round trip
        // overlaps the reading the reader was going to do anyway. Its leave edge is what reopens the
        // consumption gate — that edge is load-bearing, not bookkeeping.
        const prefetchObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        triggerLoadMore("prefetch");
                    } else {
                        sentinelLeftSinceLastFetchRef.current = true;
                    }
                }
            },
            { root: null, rootMargin: paginationRootMargin, threshold: 0.1 },
        );

        // The BOTTOM trigger, exempt from the consumption gate by design. Without it a page shorter
        // than the prefetch distance never lets the sentinel leave, the gate stays shut, and the reader
        // sits at a bottom that never advances. This observer is the guarantee that the gate can only
        // ever cost a prefetch, never a load the reader has genuinely run out of content for.
        const bottomObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        triggerLoadMore("bottom");
                    }
                }
            },
            { root: null, rootMargin: BOTTOM_TRIGGER_ROOT_MARGIN, threshold: 0.1 },
        );

        prefetchObserverRef.current = prefetchObserver;
        bottomObserverRef.current = bottomObserver;

        try {
            prefetchObserver.observe(node);
            bottomObserver.observe(node);
        } catch {
        }

        return () => {
            for (const [ref, observer] of [
                [prefetchObserverRef, prefetchObserver] as const,
                [bottomObserverRef, bottomObserver] as const,
            ]) {
                try {
                    observer.disconnect();
                } catch {
                } finally {
                    if (ref.current === observer) {
                        ref.current = null;
                    }
                }
            }
        };
    }, [dedupeOnlyPageCount, error, items.length, paginationMode, paginationRootMargin, triggerLoadMore]);

    // --- Native resume (round 12 slice 1m). See `nativeResumeRefresh`; mechanism in `nativeResumeHeadCheck`. ---
    const containerRef = React.useRef<HTMLDivElement | null>(null);

    /** Replace the accumulated pages with a fresh page 1 and restart pagination from it. */
    const swapInFreshFirstPage = React.useCallback((fresh: T[]) => {
        requestedPaginationKeysRef.current.clear();
        lastFailedRequestRef.current = null;
        itemsRef.current = fresh;
        nextCursorRef.current = tailCursorOf<T, P>(fresh);
        sentinelLeftSinceLastFetchRef.current = false;
        setError(null);
        setItems(fresh);
        setHasMore(inferHasMoreFromFirstPage<T, P>(fresh, pageSize));
    }, [pageSize, setError, setHasMore]);

    const fetchFreshFirstPage = React.useCallback(() => fetchPage(undefined), [fetchPage]);
    const getCurrentItems = React.useCallback(() => itemsRef.current, []);
    const isResumeCheckBlocked = React.useCallback(() => loadingRef.current, []);

    const { heldFreshItems: heldFreshPage, showHeldFreshItems: showHeldFreshPage } = useNativeResumeHeadCheck<T>({
        enabled: nativeResumeRefresh != null,
        onWebTabReturn: nativeResumeRefresh?.onWebTabReturn === true,
        fetchFresh: fetchFreshFirstPage,
        getId,
        getCurrentItems,
        shouldSkip: isResumeCheckBlocked,
        swapIn: swapInFreshFirstPage,
        containerRef,
        logLabel: "PaginatedListWrapperV2",
    });

    const visibleItemsCount = React.useMemo(() => {
        const rawCount = getVisibleItemsCount ? getVisibleItemsCount(items) : items.length;
        return Math.max(0, rawCount);
    }, [getVisibleItemsCount, items]);

    const phase: PaginatedListPhase = React.useMemo(() => {
        const hasVisibleItems = visibleItemsCount > 0;
        const hasAnyItems = items.length > 0;
        const busy = loading;

        if (!hasVisibleItems && busy) {
            if (hasAnyItems) {
                return "loading-more";
            }
            return "initial-loading";
        }
        if (!hasVisibleItems && error) {
            if (hasAnyItems) {
                return "load-more-error";
            }
            return "initial-error";
        }
        // Zero VISIBLE items after a completed load is "empty", however many hidden rows came back and
        // whether or not more pages exist (DASHBOARD-77, round 38 A1). This used to answer "end" when the
        // hidden rows were the last page, so a Series tab holding only the hidden bookmarks collection
        // showed a count over a blank column and never its empty state. Every `getVisibleItemsCount`
        // caller inherits this: a list whose rows were all removed in place now shows its empty panel.
        if (!hasVisibleItems && initialLoadCompleted) {
            return "empty";
        }
        if (hasVisibleItems && busy) {
            return "loading-more";
        }
        if (hasVisibleItems && error) {
            return "load-more-error";
        }
        if (hasVisibleItems && !hasMore) {
            return "end";
        }
        return "ready";
    }, [error, hasMore, initialLoadCompleted, items.length, loading, visibleItemsCount]);

    // Round 40 D10 — see `onEmptinessSettled`. Read through a ref so an inline arrow does not re-fire it,
    // and deduped by the last value SENT, so a phase change that keeps the answer (end → loading-more →
    // end) reports nothing. Loading and error phases have no answer and send nothing.
    const onEmptinessSettledRef = React.useRef(onEmptinessSettled);
    React.useEffect(() => {
        onEmptinessSettledRef.current = onEmptinessSettled;
    }, [onEmptinessSettled]);
    const lastSettledEmptinessRef = React.useRef<boolean | null>(null);
    React.useEffect(() => {
        if (phase === "initial-loading" || phase === "initial-error") {
            return;
        }
        const empty = phase === "empty";
        if (lastSettledEmptinessRef.current === empty) {
            return;
        }
        lastSettledEmptinessRef.current = empty;
        onEmptinessSettledRef.current?.(empty);
    }, [phase]);

    const renderProps = React.useMemo<PaginatedRenderPropsV2<T>>(() => ({
        items,
        loading,
        hasMore,
        error,
        phase,
        visibleItemsCount,
        onLoadMore: () => loadMore(),
        onRetry: retry,
        setItems,
    }), [error, hasMore, items, loadMore, loading, phase, retry, visibleItemsCount]);

    const statusContent = React.useMemo(() => {
        switch (phase) {
            case "initial-loading":
                return resolveStatusSlot(statusSlots?.initialLoading, renderProps);
            case "empty":
                return resolveStatusSlot(statusSlots?.empty, renderProps);
            case "loading-more":
                return resolveStatusSlot(statusSlots?.loadingMore, renderProps);
            case "end":
                return resolveStatusSlot(statusSlots?.end, renderProps);
            case "initial-error":
                return resolveStatusSlot(statusSlots?.initialError, renderProps);
            case "load-more-error":
                return resolveStatusSlot(statusSlots?.loadMoreError, renderProps);
            default:
                return null;
        }
    }, [phase, renderProps, statusSlots]);

    return (
        <div className={className} ref={containerRef}>
            {nativeResumeRefresh && heldFreshPage !== null ? (
                <NativeResumeNewItemsPill label={nativeResumeRefresh.newItemsLabel} onClick={showHeldFreshPage} />
            ) : null}
            {render(renderProps)}
            {statusContent}
            {paginationMode === "scroll" ? (
                <div
                    ref={sentinelRef}
                    style={{ width: "100%", height: "1px" }}
                    aria-hidden="true"
                    data-testid="paginated-list-sentinel"
                />
            ) : null}
        </div>
    );
}
